import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ModerationNav } from '../components/ModerationNav';
import { fetchUserHistory, reinstateUser, type MeResponse, type ModUserHistory } from '../lib/api';
import { promptDialog } from '../lib/dialog';

function label(value: string): string {
  return value.replace(/_/g, ' ');
}

/** "3 days ago" / "just now" / "2 months ago" — quick visual cue for
 *  how fresh a timestamp is. Pairs with a full date in the value
 *  slot for precision. */
function fmtRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const seconds = Math.max(0, Math.floor((now - then) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;
  const years = Math.floor(days / 365);
  return `${years} year${years === 1 ? '' : 's'} ago`;
}

interface OpusBlock {
  decision: string;
  effective_decision: string;
  confidence: number;
  reasoning: string;
  category_correction?: string | null;
  reviewed_at?: string;
}

/**
 * Moderator drill-through for a single user. Aggregates every signal
 * the moderator might need before deciding to reinstate, warn again,
 * or close the case:
 *
 *   - Account status (active / suspended / banned / deleted)
 *   - Membership in this community + tags + join date
 *   - All moderation_event rows where this user is the subject —
 *     with the flagged message body, Opus's reasoning, the resolution
 *     note, and a link back to the full event detail page
 *   - All audit_log entries targeting them (or their events)
 *   - All conversations they participate in, with peer nickname,
 *     status, and end reason
 *
 * Reached from: Restricted Users rows, Audit Log rows (user target),
 * and the subject line on ModerationEvent.
 */
export function ModerationUser({ me }: { me: MeResponse }) {
  const { userId = '' } = useParams();
  const communityId = me.memberships[0]?.communityId ?? '';
  const [history, setHistory] = useState<ModUserHistory | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [reinstating, setReinstating] = useState(false);

  const load = useCallback(() => {
    if (!communityId || !userId) return;
    fetchUserHistory(communityId, userId)
      .then((h) => {
        if (h) setHistory(h);
        else setNotFound(true);
      })
      .catch(() => setError('Could not load this user.'));
  }, [communityId, userId]);

  useEffect(load, [load]);

  async function doReinstate() {
    if (!history) return;
    const note = await promptDialog({
      title: `Reinstate ${history.user.name || history.user.email}?`,
      message: `They are currently ${history.user.status}. Reinstating flips user.status back to active; ended conversations stay ended.`,
      inputLabel: 'Audit-log note (optional)',
      placeholder: 'Why are you reinstating them?',
      multiline: true,
      confirmLabel: 'Reinstate',
    });
    if (note === null) return;
    setReinstating(true);
    setError(null);
    const result = await reinstateUser(communityId, history.user.id, note || undefined);
    setReinstating(false);
    if (result.ok) load();
    else setError(result.error);
  }

  if (notFound) {
    return (
      <main className="wide">
        <div className="card">
          <ModerationNav />
          <p>User not found in this community.</p>
          <p>
            <Link to="/moderation">← Back to the queue</Link>
          </p>
        </div>
      </main>
    );
  }

  if (!history) {
    return (
      <main className="wide">
        <div className="card">
          <ModerationNav />
          <p className="muted">Loading…</p>
          {error && <p className="error">{error}</p>}
        </div>
      </main>
    );
  }

  const restricted = history.user.status === 'suspended' || history.user.status === 'banned';

  return (
    <main className="wide">
      <div className="card">
        <ModerationNav />
        <p className="step-label">
          <Link to="/moderation">← Review queue</Link>
        </p>

        <div className="user-header">
          <div>
            <h1>{history.user.name || history.user.email}</h1>
            <p className="muted">
              {history.user.email} · account{' '}
              <strong className={`status-pill status-${history.user.status}`}>
                {history.user.status}
              </strong>
            </p>
          </div>
          {restricted && (
            <button type="button" disabled={reinstating} onClick={() => void doReinstate()}>
              {reinstating ? 'Reinstating…' : 'Reinstate this user'}
            </button>
          )}
        </div>
        {error && <p className="error">{error}</p>}

        <section className="user-section">
          <h2>Profile</h2>
          <ul className="profile-grid">
            <li>
              <span className="profile-label">Account created</span>
              <span className="profile-value">
                {fmtRelative(history.user.createdAt)}{' '}
                <span className="muted">
                  ({new Date(history.user.createdAt).toLocaleDateString()})
                </span>
              </span>
            </li>
            <li>
              <span className="profile-label">Last seen</span>
              <span className="profile-value">
                {history.stats.lastSeenAt ? (
                  <>
                    {fmtRelative(history.stats.lastSeenAt)}{' '}
                    <span className="muted">
                      ({new Date(history.stats.lastSeenAt).toLocaleString()})
                    </span>
                  </>
                ) : (
                  <span className="muted">no recorded session</span>
                )}
              </span>
            </li>
            <li>
              <span className="profile-label">Age verification</span>
              <span className="profile-value">
                {label(history.user.ageVerificationStatus)}{' '}
                {history.user.isAdult ? (
                  <span className="muted">· adult</span>
                ) : (
                  <span className="status-pill status-banned">not verified adult</span>
                )}
              </span>
            </li>
            <li>
              <span className="profile-label">Platform role</span>
              <span className="profile-value">
                {history.user.isFounder && (
                  <span className="status-pill status-active">founder</span>
                )}{' '}
                {history.user.isModerator && (
                  <span className="status-pill status-active">moderator</span>
                )}
                {!history.user.isFounder && !history.user.isModerator && (
                  <span className="muted">member</span>
                )}
              </span>
            </li>
          </ul>
        </section>

        <section className="user-section">
          <h2>Activity in this community</h2>
          <ul className="profile-grid">
            <li>
              <span className="profile-label">Messages sent</span>
              <span className="profile-value">{history.stats.lifetimeMessagesSent}</span>
            </li>
            <li>
              <span className="profile-label">Reports they filed</span>
              <span className="profile-value">
                {history.stats.reportsFiledByUser}{' '}
                {history.stats.reportsFiledByUser > 0 && (
                  <span className="muted">(pro-social signal)</span>
                )}
              </span>
            </li>
            <li>
              <span className="profile-label">Prior actions against them</span>
              <span className="profile-value">
                {history.stats.priorActionsAgainstUser === 0 ? (
                  <span>0</span>
                ) : (
                  <span
                    className={
                      history.stats.priorActionsAgainstUser >= 2
                        ? 'status-pill status-banned'
                        : 'status-pill status-suspended'
                    }
                  >
                    {history.stats.priorActionsAgainstUser}
                  </span>
                )}{' '}
                {history.stats.priorActionsAgainstUser >= 2 && (
                  <span className="muted">repeat offender</span>
                )}
              </span>
            </li>
            <li>
              <span className="profile-label">Total events</span>
              <span className="profile-value">{history.events.length}</span>
            </li>
          </ul>
        </section>

        <section className="user-section">
          <h2>Memberships</h2>
          {history.memberships.map((m) => (
            <p key={m.id}>
              <strong>{m.nickname}</strong> — {m.status}, joined{' '}
              {new Date(m.joinedAt).toLocaleDateString()}, tags:{' '}
              {m.tags.length > 0 ? m.tags.map((t) => label(t)).join(', ') : '(none)'}
            </p>
          ))}
        </section>

        <section className="user-section">
          <h2>
            Moderation events <span className="muted">({history.events.length})</span>
          </h2>
          {history.events.length === 0 ? (
            <p className="muted">No moderation events for this user.</p>
          ) : (
            <ul className="user-event-list">
              {history.events.map((e) => {
                const opus = (e.detail as { opus?: OpusBlock } | null)?.opus;
                return (
                  <li key={e.id} className={`user-event sev-${e.severity}`}>
                    <div className="user-event-head">
                      <span className={`badge sev-${e.severity}`}>{e.severity}</span>
                      <strong>{label(e.category)}</strong>
                      <span className="muted">· {label(e.source)}</span>
                      <span className={`status-pill status-${e.status}`}>{e.status}</span>
                      <span className="muted user-event-when">
                        {new Date(e.createdAt).toLocaleString()}
                      </span>
                    </div>
                    {e.messageBody && (
                      <blockquote className="user-event-body">{e.messageBody}</blockquote>
                    )}
                    {opus && (
                      <p className="muted user-event-opus">
                        <strong>Opus 4.7 · {opus.effective_decision}</strong> (
                        {(opus.confidence * 100).toFixed(0)}%): {opus.reasoning}
                      </p>
                    )}
                    {e.resolution && <p className="muted">Resolution: {e.resolution}</p>}
                    <p className="user-event-link">
                      <Link to={`/moderation/events/${e.id}`}>
                        Open event detail + conversation context →
                      </Link>
                    </p>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section className="user-section">
          <h2>
            Conversations <span className="muted">({history.conversations.length})</span>
          </h2>
          {history.conversations.length === 0 ? (
            <p className="muted">No conversations yet.</p>
          ) : (
            <ul className="bars">
              {history.conversations.map((conv) => (
                <li key={conv.id}>
                  <span className="bars-label">{conv.peerNickname}</span>
                  <span className="bars-meta">
                    {conv.status}
                    {conv.endReason && ` · ${label(conv.endReason)}`}
                    {conv.lastMessageAt &&
                      ` · last ${new Date(conv.lastMessageAt).toLocaleDateString()}`}
                  </span>
                  <span className="bars-value">
                    <code className="muted">{conv.id.slice(0, 8)}</code>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="user-section">
          <h2>
            Audit trail <span className="muted">({history.auditEntries.length})</span>
          </h2>
          {history.auditEntries.length === 0 ? (
            <p className="muted">No audit entries.</p>
          ) : (
            <ul className="audit-list">
              {history.auditEntries.map((a) => (
                <li key={a.id} className="audit-row">
                  <div className="audit-head">
                    <strong>{label(a.action)}</strong>
                    <span className="muted">
                      {a.targetType}:{a.targetId.slice(0, 8)}
                    </span>
                    <span className="muted audit-when">
                      {new Date(a.createdAt).toLocaleString()}
                    </span>
                  </div>
                  {a.metadata !== null && a.metadata !== undefined && (
                    <pre className="audit-meta">{JSON.stringify(a.metadata, null, 2)}</pre>
                  )}
                  {a.eventId && (
                    <p className="audit-link">
                      <Link to={`/moderation/events/${a.eventId}`}>
                        Open the related moderation event →
                      </Link>
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </main>
  );
}
